// Utility-Funktionen für Schedule-Tooltip

import { consolidateToRanges, parseHHMM, parseRangeEnd } from "./timeSlots";

/**
 * Wochentage-Mapping (wie in App.js definiert)
 */
export const DAY_NAMES = ["MONTAG", "DIENSTAG", "MITTWOCH", "DONNERSTAG", "FREITAG", "SAMSTAG", "SONNTAG"];
export const DAY_SHORT_NAMES = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

/** Eine Zuordnung eines Studenten zu einem Slot in einem gespeicherten Plan. */
export interface ScheduleAssignment {
  day: string;
  dayIndex: number;
  time: string;
}

/** Gespeicherter Plan (DB-Zeile) — hier interessiert nur available_times. */
interface ScheduleLike {
  available_times?: Record<string, unknown> | null;
}

/**
 * Extrahiert Zeit-Zuordnungen aus Schedules für einen Studenten.
 * @returns Array von Zuordnungen: [{ schedule: {...}, assignments: [{ day, time, dayIndex }] }]
 */
export function extractScheduleAssignments<S extends ScheduleLike>(
  schedules: readonly S[] | null | undefined,
  studentMemberIds: readonly string[] | null | undefined,
): Array<{ schedule: S; assignments: ScheduleAssignment[] }> {
  if (!schedules || schedules.length === 0 || !studentMemberIds || studentMemberIds.length === 0) {
    return [];
  }

  const assignments: Array<{ schedule: S; assignments: ScheduleAssignment[] }> = [];

  schedules.forEach(schedule => {
    const scheduleAssignments: ScheduleAssignment[] = [];
    const availableTimes = schedule.available_times || {};

    // Durchlaufe alle Tage
    Object.entries(availableTimes).forEach(([dayIndexStr, timeSlots]) => {
      const dayIndex = parseInt(dayIndexStr);
      const dayName = DAY_NAMES[dayIndex];

      if (!dayName || !Array.isArray(timeSlots)) {
        return;
      }

      // Durchlaufe alle Zeit-Slots des Tages
      timeSlots.forEach((slot: unknown) => {
        if (Array.isArray(slot) && slot.length >= 2) {
          const time: unknown = slot[0];
          const slotMemberId: unknown = slot[1];

          // Prüfe, ob dieser Slot zu unserem Studenten gehört
          if (
            typeof slotMemberId === 'string' &&
            typeof time === 'string' &&
            time &&
            studentMemberIds.includes(slotMemberId)
          ) {
            scheduleAssignments.push({
              day: dayName,
              dayIndex: dayIndex,
              time: time
            });
          }
        }
      });
    });

    // Nur hinzufügen, wenn es Zuordnungen gibt
    if (scheduleAssignments.length > 0) {
      assignments.push({
        schedule: schedule,
        assignments: scheduleAssignments
      });
    }
  });

  return assignments;
}

/**
 * Konvertiert Zeit-String (z.B. "9.00") zu formatiertem String (z.B. "9:00")
 */
function formatTime(timeStr: string): string {
  if (!timeStr) return '';

  // Ersetze Punkt durch Doppelpunkt
  return timeStr.replace('.', ':');
}

/**
 * Formatiert einen Zeit-Bereich kompakt (z.B. "Mo 9:00-10:30")
 * Gruppiert zusammenhängende Zeiten pro Tag
 * @returns Array von formatierten Zeit-Bereichen: ["Mo 9:00-10:30", "Di 14:00-15:00"]
 */
export function formatTimeRanges(assignments: readonly ScheduleAssignment[] | null | undefined): string[] {
  if (!assignments || assignments.length === 0) {
    return [];
  }

  // Gruppiere nach Tag
  const byDay: Record<number, string[]> = {};
  assignments.forEach(assignment => {
    const dayIndex = assignment.dayIndex;
    if (!byDay[dayIndex]) {
      byDay[dayIndex] = [];
    }
    byDay[dayIndex].push(assignment.time);
  });

  const ranges: string[] = [];

  // Verarbeite jeden Tag
  Object.keys(byDay).sort((a, b) => parseInt(a) - parseInt(b)).forEach(dayIndexStr => {
    const dayIndex = parseInt(dayIndexStr);
    const dayShortName = DAY_SHORT_NAMES[dayIndex];
    const times = [...new Set(byDay[dayIndex])];

    // Intervall-Erkennung kommt aus dem geteilten timeSlots-Modul (ersetzt eine
    // von vormals 4 unabhängigen Reimplementierungen desselben Algorithmus).
    consolidateToRanges(times).forEach(({ start, end }) => {
      // Ein einzelner 15-Min-Slot wird als Zeitpunkt statt als Bereich angezeigt
      // (eine Bereichsdauer von genau 15 Minuten kann nur von einem einzelnen
      // Slot stammen, da consolidateToRanges nur zusammenhängende Slots gruppiert).
      const [startH, startM] = parseHHMM(start);
      // Ende ist exklusiv und kann "24.00" sein (Block bis 23.45) — parseHHMM
      // wirft auf Stunde 24, parseRangeEnd nicht.
      const [endH, endM] = parseRangeEnd(end);
      const isSingleSlot = (endH * 60 + endM) - (startH * 60 + startM) === 15;
      if (isSingleSlot) {
        ranges.push(`${dayShortName} ${formatTime(start)}`);
      } else {
        ranges.push(`${dayShortName} ${formatTime(start)}-${formatTime(end)}`);
      }
    });
  });

  return ranges;
}

/**
 * Formatiert ein Datum kompakt
 * @returns Formatierter Datum-String (z.B. "15.01.2024")
 */
export function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '';

  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch (e) {
    return dateStr;
  }
}
